
import { Component, Ref, Vue } from 'vue-property-decorator'
import { SideMenuTopic } from '@/const/SideMenuTopic'
import ImageBase from '@/components/atoms/ImageBase.vue'
import ButtonBase from '@/components/atoms/ButtonBase.vue'
import ButtonNavigation from '@/components/molecules/ButtonNavigation.vue'
import SubjectSelectButton from '@/components/organisms/SubjectSelectButton.vue'
import GradeSelectButton from '@/components/organisms/GradeSelectButton.vue'
import SidemenuInformationPanel from '@/components/organisms/SidemenuInformationPanel.vue'
import ModalEditStudentData from '@/views/modal/ModalEditStudentData.vue'
import ModalEditUserPassword from '@/views/modal/ModalEditUserPassword.vue'
import ModalEditStudentMailAddress from '@/views/modal/ModalEditStudentMailAddress.vue'
import ModalSelectSubject from '@/views/modal/ModalSelectSubject.vue'
import ModalSelectGrade from '@/views/modal/ModalSelectGrade.vue'
import { SubjectCode } from '@/const/SubjectCode'
import { GradeCode } from '@/const/GradeCode'
import { UserRole } from '@/const/UserRole'

/**
 * 左側メニュー
 *
 * template部品のルート要素で使用し、メイン部分に表示する内容をslotで渡す
 *
 * ex)
 * sidemenu-layout.any-template
 *   .any-template__item 右側メイン部分に表示する内容
 */
@Component({
  components: {
    ImageBase,
    ButtonBase,
    ButtonNavigation,
    SubjectSelectButton,
    GradeSelectButton,
    SidemenuInformationPanel,
    ModalEditStudentData,
    ModalEditUserPassword,
    ModalEditStudentMailAddress,
    ModalSelectSubject,
    ModalSelectGrade,
  },
})
export default class SidemenuLayout extends Vue {
  /** 生徒情報編集モーダル */
  @Ref()
  modalEditStudentData!: ModalEditStudentData

  /** パスワード編集モーダル */
  @Ref()
  modalEditUserPassword!: ModalEditUserPassword

  /** メールアドレス変更モーダル */
  @Ref()
  modalEditStudentMailAddress!: ModalEditStudentMailAddress

  /** 教科選択モーダル */
  @Ref()
  modalSelectSubject!: ModalSelectSubject

  /** 学年選択モーダル */
  @Ref()
  modalSelectGrade!: ModalSelectGrade

  /** 権限 */
  private readonly role = UserRole.STUDENT

  /** 現在ページのURL情報 */
  private url = new URL(window.location.href)

  /** メニューの展開状況 */
  private isOpen = true

  /** 現在のビューモード(light or dark) */
  private darkMode = false

  /** 現在の教科情報 */
  private currentSubjectMaster: SubjectCode.SubjectMaster | null = null

  /** 現在の学年情報 */
  private currentGradeMaster: GradeCode.GradeMaster | null = null

  /**
   * 初期処理
   */
  private async mounted(): Promise<void> {
    // 教科取得
    const subjectCode = await this.$appCookies.studentMenuState.subjectCode.get()
    // undefinedブロックでnullを設定しているが、クッキー処理内でデフォルト値を設定しているため実際は発生しない
    this.currentSubjectMaster = SubjectCode.findByCode(subjectCode) || null

    // 学年取得
    const gradeCode = await this.$appCookies.studentMenuState.gradeCode.get()
    // undefinedブロックでnullを設定しているが、クッキー処理内でデフォルト値を設定しているため実際は発生しない
    this.currentGradeMaster = GradeCode.findByCode(gradeCode) || null

    // TODO apiから新着(未読)のお知らせ情報を取得する処理を追加
  }

  /**
   * メニュー項目一覧
   *
   * @returns 項目リスト
   */
  private get topicList(): SideMenuTopic.Topic[] {
    return SideMenuTopic.LIST
  }

  /**
   * 新着お知らせの有無
   *
   * TODO 新着お知らせ情報取得API実装後に修正
   *
   * @returns 新着があればtrue
   */
  private get hasNewNotice(): boolean {
    // api実装まではfalseを返し非表示
    return false
  }

  /**
   * 対象項目が現在表示中のページかを判定する
   *
   * @param url チェック対象のURL
   * @returns 表示中のページとURLが一致するか
   */
  private isCurrentPage(url: string): boolean {
    return url === this.url.pathname
  }

  /**
   * メニューの表示切り替え
   *
   * @param isOpen メニューの表示/非表示
   */
  private toggleMenu(isOpen: boolean): void {
    this.isOpen = isOpen
  }

  /**
   * 教科選択ボタン押下時の処理
   */
  private onSelectSubjectButton(): void {
    // nullブロックで空文字を送っているが、実際は発生しない
    this.modalSelectSubject.show(this.currentSubjectMaster?.code || '')
  }

  /**
   * 教科選択時の処理
   *
   * @param code 教科コード
   */
  private async eventSubmitSubject(code: string): Promise<void> {
    // 変更されていない場合は終了
    if (code === this.currentSubjectMaster?.code) {
      return
    }
    await this.$appCookies.studentMenuState.subjectCode.set(code)
    // 自画面に遷移(VueRouterの警告回避のため、hrefを使用して強制的に遷移)
    window.location.href = this.$route.fullPath
  }

  /**
   * 学年選択ボタン押下時の処理
   */
  private onSelectGradeButton(): void {
    // nullブロックで空文字を送っているが、実際は発生しない
    this.modalSelectGrade.show(this.currentGradeMaster?.code || '')
  }

  /**
   * 学年選択時の処理
   *
   * @param code 学年コード
   */
  private async eventSubmitGrade(code: string): Promise<void> {
    // 変更されていない場合は終了
    if (code === this.currentGradeMaster?.code) {
      return
    }
    await this.$appCookies.studentMenuState.gradeCode.set(code)
    // 自画面に遷移(VueRouterの警告回避のため強制的に遷移)
    window.location.href = this.$route.fullPath
  }

  /**
   * ビューモードを切り替える
   * TODO ダークモード実装後に修正
   */
  private toggleViewMode(): void {
    alert('ダークモード切り替え')
    this.darkMode = !this.darkMode
  }

  /**
   * 生徒情報：編集ボタン押下時の処理
   */
  private onEditButton(): void {
    this.modalEditStudentData.show()
  }

  /**
   * 生徒情報：ログアウトボタン押下時の処理
   */
  private onLogoutButton(): void {
    if (!confirm('ログアウトしますか？')) {
      return
    }
    // クッキー情報を削除
    this.$appCookies.removeAuth()
    // ログイン画面に遷移
    this.$router.push('/student/login')
  }

  /**
   * 生徒情報：パスワード変更ボタン押下時の処理
   */
  private onChangePasswordButton(): void {
    this.modalEditUserPassword.show()
  }

  /**
   * 生徒情報：メールアドレス変更ボタン押下時の処理
   */
  private onChangeMailAddressButton(): void {
    this.modalEditStudentMailAddress.show()
  }
}
